import { useId, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DateInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { UserPicker } from '@/components/ui/user-picker';
import { useUserSearch } from '@/hooks/useUserSearch';
import type {
  PatientTaskPriority,
  PatientTaskStatus,
  PatientTaskType,
} from '@/features/patient-record/types';

import { DueDateQuickPicks } from './DueDateQuickPicks';
import { TASK_PRIORITIES, TASK_STATUSES, TASK_TYPES } from './taskHelpers';
import type { TaskFormValues } from './TaskFormFields.types';

interface TaskFormFieldsProps {
  readonly values: TaskFormValues;
  readonly onChange: (next: TaskFormValues) => void;
  /** Edit mode shows the Status row; create mode hides it (always TODO). */
  readonly showStatus: boolean;
  readonly disabled?: boolean;
}

/**
 * Shared field block for the create + edit task dialogs (PTV2-1097).
 *
 * Assignee picker reuses `UserPicker` + `useUserSearch` to match the Care
 * Team editor flow; the picker is uncontrolled w.r.t. the search query so
 * each parent owns its own typeahead state via the `assignee` value.
 */
export function TaskFormFields({
  values,
  onChange,
  showStatus,
  disabled = false,
}: TaskFormFieldsProps): ReactElement {
  const { t } = useTranslation();
  const titleId = useId();
  const typeId = useId();
  const assigneeId = useId();
  const priorityId = useId();
  const statusId = useId();
  const dueId = useId();
  const descId = useId();
  const notesId = useId();

  const [assigneeQuery, setAssigneeQuery] = useState('');
  const assigneeSearch = useUserSearch(assigneeQuery);

  const set = <K extends keyof TaskFormValues>(key: K, value: TaskFormValues[K]): void => {
    onChange({ ...values, [key]: value });
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-3">
      <Field
        htmlFor={titleId}
        label={t('patient.tasks.fieldTitle')}
        required
        className="sm:col-span-2"
      >
        <Input
          id={titleId}
          type="text"
          value={values.title}
          onChange={(event) => {
            set('title', event.target.value);
          }}
          disabled={disabled}
          placeholder={t('patient.tasks.fieldTitlePlaceholder')}
          className="h-9"
        />
      </Field>

      <Field htmlFor={typeId} label={t('patient.tasks.fieldType')}>
        <Select
          id={typeId}
          value={values.type}
          disabled={disabled}
          onChange={(event) => {
            set('type', event.target.value as PatientTaskType);
          }}
          className="h-9 text-sm"
        >
          {TASK_TYPES.map((value) => (
            <option key={value} value={value}>
              {t(`patient.tasks.type.${value}`)}
            </option>
          ))}
        </Select>
      </Field>

      <Field htmlFor={priorityId} label={t('patient.tasks.fieldPriority')}>
        <Select
          id={priorityId}
          value={values.priority}
          disabled={disabled}
          onChange={(event) => {
            set('priority', event.target.value as PatientTaskPriority);
          }}
          className="h-9 text-sm"
        >
          {TASK_PRIORITIES.map((value) => (
            <option key={value} value={value}>
              {t(`patient.tasks.priority.${value}`)}
            </option>
          ))}
        </Select>
      </Field>

      <Field
        htmlFor={assigneeId}
        label={t('patient.tasks.fieldAssignee')}
        className={showStatus ? '' : 'sm:col-span-2'}
      >
        <UserPicker
          id={assigneeId}
          value={values.assignee}
          onChange={(option) => {
            set('assignee', option);
          }}
          options={assigneeSearch.results}
          isLoading={assigneeSearch.isLoading}
          ariaLabel={t('patient.tasks.fieldAssignee')}
          placeholder={t('patient.tasks.fieldAssigneePlaceholder')}
          query={assigneeQuery}
          onQueryChange={setAssigneeQuery}
          disabled={disabled}
        />
      </Field>

      {showStatus && (
        <Field htmlFor={statusId} label={t('patient.tasks.fieldStatus')}>
          <Select
            id={statusId}
            value={values.status}
            disabled={disabled}
            onChange={(event) => {
              set('status', event.target.value as PatientTaskStatus);
            }}
            className="h-9 text-sm"
          >
            {TASK_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`patient.tasks.status.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
      )}

      <Field
        htmlFor={dueId}
        label={t('patient.tasks.fieldDueDate')}
        className="sm:col-span-2"
      >
        <div className="flex flex-wrap items-center gap-2">
          <DateInput
            id={dueId}
            value={values.dueDate}
            onChange={(value) => {
              set('dueDate', value);
            }}
            disabled={disabled}
          />
          <DueDateQuickPicks
            value={values.dueDate}
            disabled={disabled}
            onPick={(next) => {
              set('dueDate', next);
            }}
          />
        </div>
      </Field>

      <Field
        htmlFor={descId}
        label={t('patient.tasks.fieldDescription')}
        className="sm:col-span-2"
      >
        <Textarea
          id={descId}
          value={values.description}
          rows={2}
          onChange={(event) => {
            set('description', event.target.value);
          }}
          disabled={disabled}
          placeholder={t('patient.tasks.fieldDescriptionPlaceholder')}
          className="resize-none"
        />
      </Field>

      <Field
        htmlFor={notesId}
        label={t('patient.tasks.fieldNotes')}
        className="sm:col-span-2"
      >
        <Textarea
          id={notesId}
          value={values.notes}
          rows={2}
          onChange={(event) => {
            set('notes', event.target.value);
          }}
          disabled={disabled}
          placeholder={t('patient.tasks.fieldNotesPlaceholder')}
          className="resize-none"
        />
      </Field>
    </div>
  );
}

interface FieldProps {
  readonly label: string;
  readonly htmlFor: string;
  readonly required?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}

function Field({
  label,
  htmlFor,
  required = false,
  className = '',
  children,
}: FieldProps): ReactElement {
  return (
    <div className={`block space-y-1 ${className}`}>
      <label
        htmlFor={htmlFor}
        className="block text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
      >
        {label}
        {required && (
          <span className="text-destructive ms-0.5" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

